import type { PatientCoverage } from '~/lib/constants/patients.constants';

export enum PatientCoveragePriority {
  PRIMARY = 1,
  SECONDARY = 2,
  TERTIARY = 3,
}

export enum PatientCoveragePriorityToText {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
  TERTIARY = 'Tertiary',
}

export const patientCoveragePriorityOptionsFullList = [
  {
    node: PatientCoveragePriorityToText.PRIMARY,
    value: PatientCoveragePriority.PRIMARY.toString(),
  },
  {
    node: PatientCoveragePriorityToText.SECONDARY,
    value: PatientCoveragePriority.SECONDARY.toString(),
  },
  {
    node: PatientCoveragePriorityToText.TERTIARY,
    value: PatientCoveragePriority.TERTIARY.toString(),
  },
];
/** Function to get a key based on a value */
export const getPatientCoveragePriorityKeyByValue = (value) => {
  const foundKey = Object.keys(PatientCoveragePriority).find(
    (key) => PatientCoveragePriority[key] === value,
  );

  return foundKey ?? null;
};

/** Function to get text based on a value*/
export const getPatientCoveragePriorityTextByValue = (value) =>
  PatientCoveragePriorityToText[getPatientCoveragePriorityKeyByValue(value)] ||
  null;

export const getExistingCoveragePriorities = (
  patientCoverages: PatientCoverage[],
) =>
  patientCoverages
    ?.filter(
      (coverage) =>
        coverage.coveragePriority !== undefined &&
        coverage.coveragePriority !== null &&
        coverage.isActive,
    )
    .map((coverage) => coverage.coveragePriority) || [];

export const isCurrentCoveragePriorityAlreadyExists = (
  patientCoverages: PatientCoverage[],
  currentCoverage: PatientCoverage,
) => {
  const existingCoveragePrioritiesWithoutCurrentCoverage = (
    patientCoverages ?? []
  ).filter(
    ({ coverageExternalId, coveragePriority, isActive }) =>
      coveragePriority !== null &&
      isActive &&
      coverageExternalId !== currentCoverage?.coverageExternalId,
  );

  return existingCoveragePrioritiesWithoutCurrentCoverage.some(
    ({ coveragePriority }) =>
      coveragePriority === currentCoverage?.coveragePriority,
  );
};

export const getPatientCoveragePriorityOptions = (
  patientCoverages: PatientCoverage[],
) => {
  const existingCoveragePriorities =
    getExistingCoveragePriorities(patientCoverages);

  if (existingCoveragePriorities?.length === 0) {
    return patientCoveragePriorityOptionsFullList.slice(0, 1);
  }

  if (
    existingCoveragePriorities?.length === 1 &&
    existingCoveragePriorities.includes(PatientCoveragePriority.PRIMARY)
  ) {
    return patientCoveragePriorityOptionsFullList.slice(0, 2);
  }

  return patientCoveragePriorityOptionsFullList;
};

export const getSortedCoverageListForPatient = (
  patientCoverageList: PatientCoverage[],
) => {
  const sortedList = patientCoverageList.slice().sort((a, b) => {
    // Sort by isActive in descending order
    const isActiveComparison = Number(b.isActive) - Number(a.isActive);
    if (isActiveComparison !== 0) {
      return isActiveComparison;
    }

    // Sort by coveragePriority in ascending order, with nulls last
    const coveragePriorityA = a.coveragePriority || Number.MAX_SAFE_INTEGER;
    const coveragePriorityB = b.coveragePriority || Number.MAX_SAFE_INTEGER;
    const coveragePriorityComparison = coveragePriorityA - coveragePriorityB;
    if (coveragePriorityComparison !== 0) {
      return coveragePriorityComparison;
    }

    // Sort by payor
    return a.payor.localeCompare(b.payor);
  });

  return sortedList;
};
